import { Button, Classes } from '@blueprintjs/core';
import { useApplicationDetail } from '@axo/shared/data-access/hooks/queries/useApplicationDetail';
import { useCurrentUserHasLock } from '@axo/shared/data-access/hooks/services/lock/useApplicationLock';
import { useTransactionalCommunications } from '@axo/shared/data-access/hooks/services/ortto_communication';
import { useHasAnyOfRoles } from '@axo/shared/data-access/provider';
import { permissions } from '@axo/shared/data-access/types';
import { formatDatetime } from '../../utils/formatDate';
import { useMuteTransactionalCommunication } from './commands/useMuteTransactionalCommunication';
import { findSessionMute } from './utils/transactionalMute';
import styles from './MarketingConsentCardSection.module.css';

/**
 * "Mute for this session": stops the customer's current transactional
 * communication in Ortto (MV-3323). The muted state is read back from the
 * backend, so it survives a reload and nobody mutes twice.
 */
export function TransactionalMuteSection({
  applicationID,
  customerID,
}: {
  applicationID: string;
  customerID: string;
}) {
  const application = useApplicationDetail(applicationID);
  const country = application.data?.MarketCountry;
  const hasLock = useCurrentUserHasLock(applicationID, country);
  const canWrite = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
  ]);
  // gate-allow SM-2: single-shot read consumed in place in this leaf component, never polled.
  const records = useTransactionalCommunications(customerID);
  const mute = useMuteTransactionalCommunication();

  // A failed read shows the default button, never a false "muted" (C10).
  const sessionMute = findSessionMute(records.data, {
    country,
    applicationCreatedAt: application.data?.CreatedAt,
  });

  return (
    <div className={styles.mute}>
      <span>Transactional communication (EMAIL/SMS)</span>
      {sessionMute ? (
        <div className={styles.consentRow}>
          <Button outlined small intent="danger" disabled icon="tick">
            Muted
          </Button>
          <span className={Classes.TEXT_MUTED}>
            {formatDatetime(sessionMute.CreatedAt)}
          </span>
        </div>
      ) : (
        <div className={styles.consentRow}>
          <Button
            outlined
            small
            intent="danger"
            loading={mute.isLoading}
            disabled={!canWrite || !hasLock.data || !country}
            onClick={() => country && mute.execute({ customerID, country })}
          >
            Mute for this session
          </Button>
        </div>
      )}
    </div>
  );
}
